export type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const DAYS_OF_WEEK: readonly { key: DayOfWeek; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

export type WorkingHoursDayValue = {
  enabled: boolean;
  start: string;
  end: string;
};

export type WorkingHoursValue = Record<DayOfWeek, WorkingHoursDayValue>;

export const DEFAULT_WORKING_HOURS: WorkingHoursValue = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
} as const;

export function isValidTimeString(time: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
}

export function parseWorkingHours(input: unknown): WorkingHoursValue {
  let parsed: unknown = input;

  if (input === null || input === undefined) {
    return structuredClone(DEFAULT_WORKING_HOURS);
  }

  if (typeof input === 'string') {
    try {
      parsed = JSON.parse(input);
    } catch {
      return structuredClone(DEFAULT_WORKING_HOURS);
    }
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return structuredClone(DEFAULT_WORKING_HOURS);
  }

  const result = {} as WorkingHoursValue;
  const record = parsed as Record<string, unknown>;

  for (const { key } of DAYS_OF_WEEK) {
    const defaultDay = DEFAULT_WORKING_HOURS[key];
    const day = record[key];

    if (typeof day !== 'object' || day === null || Array.isArray(day)) {
      result[key] = { ...defaultDay };
      continue;
    }

    const dayRecord = day as Record<string, unknown>;
    const start =
      typeof dayRecord['start'] === 'string' &&
      isValidTimeString(dayRecord['start'])
        ? dayRecord['start']
        : '09:00';
    const end =
      typeof dayRecord['end'] === 'string' &&
      isValidTimeString(dayRecord['end'])
        ? dayRecord['end']
        : '17:00';
    const enabled =
      typeof dayRecord['enabled'] === 'boolean'
        ? dayRecord['enabled']
        : defaultDay.enabled;

    result[key] = { enabled, start, end };
  }

  return result;
}

export function serializeWorkingHours(value: WorkingHoursValue): string {
  return JSON.stringify(value);
}

export function validateWorkingHours(
  value: WorkingHoursValue
): string | undefined {
  const enabledDays = DAYS_OF_WEEK.filter(({ key }) => value[key].enabled);

  if (enabledDays.length === 0) {
    return 'At least one day must be enabled with valid hours';
  }

  for (const { key } of enabledDays) {
    const { start, end } = value[key];
    if (start >= end) {
      return 'End time must be after start time';
    }
  }

  return undefined;
}

/**
 * Returns a new WorkingHoursValue where Mon-Fri are overwritten with the source day's
 * { enabled, start, end }. Sat and Sun are preserved unchanged.
 */
export function copyDayToWeekdays(
  value: WorkingHoursValue,
  sourceDay: DayOfWeek
): WorkingHoursValue {
  const sourceConfig = value[sourceDay];
  return {
    ...value,
    monday: { ...sourceConfig },
    tuesday: { ...sourceConfig },
    wednesday: { ...sourceConfig },
    thursday: { ...sourceConfig },
    friday: { ...sourceConfig },
  };
}
